"use client"

import { useEffect, useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { ArrowLeft, Camera, ImageOff, Shield, Download, X, Calendar } from "lucide-react"
import Link from "next/link"
import Image from "next/image"


interface Photo {
  id: number
  filename: string
  filepath: string
  thumbnail_path?: string | null
  is_public: boolean
  created_at: string
  taken_at?: string | null
}

function formatPhotoDate(photo: Photo) {
  const dateStr = photo.taken_at || photo.created_at
  const isExif = !!photo.taken_at
  return {
    formatted: new Date(dateStr).toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    isExif,
  }
}

export default function GalleryPage() {
  const [photos, setPhotos] = useState<Photo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)

  useEffect(() => {
    fetchPhotos()
  }, [])

  const handleDownload = useCallback(async (photo: Photo) => {
    try {
      const response = await fetch(photo.filepath)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = photo.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Download failed:", err)
    }
  }, [])

  const fetchPhotos = async () => {
    try {
      const response = await fetch("/api/photos")
      const data = await response.json()
      setPhotos(data.photos || [])
    } catch (error) {
      console.error("Error fetching photos:", error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen flex-col bg-secondary/30">
      {/* Header */}
      <div className="w-full bg-card border-b border-border p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link href="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="font-bold text-xl text-foreground">Galeria Pública</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/admin/login">
              <Button variant="ghost" size="sm">
                <Shield className="mr-2 h-4 w-4" />
                Admin
              </Button>
            </Link>
            <Link href="/camera">
              <Button size="sm">
                <Camera className="mr-2 h-4 w-4" />
                Nova foto
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center space-y-3">
                <div className="w-12 h-12 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground">A carregar fotos...</p>
              </div>
            </div>
          ) : photos.length === 0 ? (
            <Card className="p-12">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto rounded-full bg-muted flex items-center justify-center">
                  <ImageOff className="h-10 w-10 text-muted-foreground" />
                </div>
                <div>
                  <h2 className="font-semibold text-xl text-foreground mb-2">Nenhuma foto ainda</h2>
                  <p className="text-muted-foreground mb-6">Sê o primeiro a tirar uma foto com o Janota!</p>
                </div>
                <Link href="/camera">
                  <Button size="lg">
                    <Camera className="mr-2 h-5 w-5" />
                    Tirar primeira foto
                  </Button>
                </Link>
              </div>
            </Card>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-muted-foreground">
                  {photos.length} {photos.length === 1 ? "foto" : "fotos"} na galeria
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {photos.map((photo) => {
                  const { formatted, isExif } = formatPhotoDate(photo)
                  return (
                    <Card
                      key={photo.id}
                      className="overflow-hidden group hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                    >
                      <div className="relative aspect-[4/3] bg-muted">
                        <Image
                          src={photo.thumbnail_path || photo.filepath || "/placeholder.svg?height=400&width=300"}
                          alt={`Foto com Janota - ${photo.filename}`}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        />
                      </div>
                      <div className="p-3 bg-card flex items-center gap-1.5">
                        <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                        <p className="text-xs text-muted-foreground">
                          {formatted}
                        </p>
                        {isExif && (
                          <span className="text-[10px] text-muted-foreground/70 ml-auto italic">EXIF</span>
                        )}
                      </div>
                    </Card>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fullscreen Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent showCloseButton={false} className="flex flex-col items-center max-w-none sm:max-w-none w-[95vw] h-[95vh] p-0 border-none bg-background/95 backdrop-blur-sm overflow-hidden">
          <DialogTitle className="sr-only">
            {selectedPhoto ? `Foto - ${selectedPhoto.filename}` : "Foto"}
          </DialogTitle>
          {selectedPhoto && (
            <>
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-3 right-3 z-10 bg-background/80 hover:bg-background rounded-full"
                onClick={() => setSelectedPhoto(null)}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Fechar</span>
              </Button>

              {/* Image */}
              <div className="relative flex-1 w-full min-h-0 mt-6">
                <Image
                  src={selectedPhoto.filepath}
                  alt={`Foto com Janota - ${selectedPhoto.filename}`}
                  fill
                  className="object-contain"
                  sizes="95vw"
                  priority
                />
              </div>

              {/* Footer with date and download */}
              <div className="flex items-center justify-between w-full px-6 py-4 shrink-0 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{formatPhotoDate(selectedPhoto).formatted}</span>
                  {formatPhotoDate(selectedPhoto).isExif && (
                    <span className="text-xs italic text-muted-foreground/70">(EXIF)</span>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleDownload(selectedPhoto)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Descarregar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  )
}
